import { ref, computed } from 'vue';

let sdkInstance = null;
let sdkPromise = null;

const isReady = ref(false);
const error = ref(null);

/**
 * Build a SignerProvider that satisfies the SDK's interface:
 *   getSigner(identifier: string) → Promise<TransactionSigner>
 *   getAddress(identifier: string) → Promise<string>
 *
 * The wallet adapter's signTransaction/signAllTransactions are the
 * TransactionSigner methods the SDK needs under the hood.
 */
function createBrowserSignerProvider(wallet) {
    return {
        async getSigner(_identifier) {
            const pubkey = wallet.publicKey?.value;
            if (!pubkey) throw new Error('Wallet not connected');
            // solana-wallets-vue exposes signTransaction etc as Refs — unwrap with .value
            const signTx = wallet.signTransaction?.value || wallet.signTransaction;
            const signAllTx = wallet.signAllTransactions?.value || wallet.signAllTransactions;
            const signAndSend = wallet.signAndSendTransaction?.value || wallet.signAndSendTransaction;
            return {
                address: pubkey.toBase58(),
                signTransaction: signTx ? (tx) => signTx(tx) : undefined,
                signAllTransactions: signAllTx ? (txs) => signAllTx(txs) : undefined,
                signAndSendTransaction: signAndSend ? (tx) => signAndSend(tx) : undefined,
            };
        },
        async getAddress(_identifier) {
            const pubkey = wallet.publicKey?.value;
            if (!pubkey) throw new Error('Wallet not connected');
            return pubkey.toBase58();
        },
    };
}

/**
 * Singleton composable wrapping createSolSdk() from @solana-compass/sdk.
 * Lazily initializes on first call to getSdk().
 *
 * RPC URL sourced from window.swapConfig.rpcEndpoint (already exposed in app.blade.php).
 * Wallet signer wired from window.wallet when available.
 */
export function useSolSdk() {
    async function getSdk() {
        if (sdkInstance) return sdkInstance;
        if (sdkPromise) return sdkPromise;

        sdkPromise = (async () => {
            try {
                const { createSolSdk, registerDefaultProviders } = await import('@solana-compass/sdk');

                const rpcUrl = window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com';
                const wallet = window.wallet;

                // Always provide the signer provider — it reads wallet state
                // lazily at call time, so it works even if wallet connects later.
                const sdk = createSolSdk({
                    rpcUrl,
                    signer: wallet ? createBrowserSignerProvider(wallet) : null,
                });

                await registerDefaultProviders(sdk);

                // Log which earn providers actually registered
                if (sdk.earn?.listProviders) {
                    console.debug('[useSolSdk] Earn providers:', sdk.earn.listProviders());
                }

                sdkInstance = sdk;
                isReady.value = true;
                return sdk;
            } catch (e) {
                error.value = e;
                console.error('[useSolSdk] Failed to initialize SDK:', e);
                throw e;
            }
        })();

        return sdkPromise;
    }

    /**
     * Reset the SDK instance — call when wallet connects/disconnects
     * so the signer provider picks up the new state.
     */
    function resetSdk() {
        sdkInstance = null;
        sdkPromise = null;
        isReady.value = false;
        error.value = null;
    }

    return {
        getSdk,
        resetSdk,
        isReady: computed(() => isReady.value),
        error: computed(() => error.value),
    };
}
